import React, { useState, useEffect } from "react";
import {
  GitBranch,
  Calendar,
  Rocket,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from "lucide-react";
import { useCase } from "../../context/CaseContext";
import { getAuditLogsForCase, AuditLogEntry } from "../../services/auditService";

interface Milestone {
  key: string;
  label: string;
  date: Date | null;
  icon: React.ReactNode;
  dotColor: string;
  done: boolean;
  interruptions?: number; // how many on_hold/stuck events happened before this
}

const toDate = (ts: any): Date | null => {
  if (!ts) return null;
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
};

const fmt = (d: Date): string =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const daysBetween = (a: Date, b: Date) =>
  Math.round(Math.abs(b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));

export default function CaseTimeline() {
  const { currentCaseId, meeting, notionPageUrl, status } = useCase();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!currentCaseId) return;
    getAuditLogsForCase(currentCaseId).then(setLogs);
  }, [currentCaseId]);

  const findStatusChange = (toStatus: string) =>
    logs.find(
      (l) =>
        l.action === "STATUS_CHANGE" &&
        l.diff?.some((d) => d.field === "status" && d.newValue === toStatus),
    );

  const countStatusChanges = (toStatus: string) =>
    logs.filter(
      (l) =>
        l.action === "STATUS_CHANGE" &&
        l.diff?.some((d) => d.field === "status" && d.newValue === toStatus),
    ).length;

  const createdLog = logs.find((l) => l.action === "CREATE");
  const meetingDate = meeting?.date ? new Date(Number(meeting.date)) : null;
  const kickoffDone = meetingDate && meetingDate < new Date();
  const inProgressLog = findStatusChange("busy");
  const transferredLog = findStatusChange("transferred");
  const blockedCount = countStatusChanges("on_hold") + countStatusChanges("stuck");

  const milestones: Milestone[] = [
    {
      key: "created",
      label: "Created",
      date: toDate(createdLog?.timestamp),
      icon: <GitBranch size={13} />,
      dotColor: "bg-slate-500",
      done: true, // case exists, so it was created
    },
    {
      key: "meeting",
      label: "Kickoff",
      date: meetingDate,
      icon: <Calendar size={13} />,
      dotColor: "bg-teal-500",
      done: !!kickoffDone,
    },
    {
      key: "inprogress",
      label: "Work Started",
      date: toDate(inProgressLog?.timestamp),
      icon: <Rocket size={13} />,
      dotColor: "bg-blue-500",
      done: !!inProgressLog,
      interruptions: blockedCount,
    },
    {
      key: "transferred",
      label: "Transferred",
      date: toDate(transferredLog?.timestamp),
      icon: <ExternalLink size={13} />,
      dotColor: "bg-purple-500",
      done: status === "transferred",
    },
  ];

  if (!currentCaseId) return null;

  const doneMilestones = milestones.filter((m) => m.done);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
          <GitBranch size={13} /> Case Timeline
          {doneMilestones.length > 0 && (
            <span className="text-[10px] font-bold bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded-full">
              {doneMilestones.length}/{milestones.length}
            </span>
          )}
        </span>
        {collapsed ? (
          <ChevronDown size={14} className="text-gray-400" />
        ) : (
          <ChevronUp size={14} className="text-gray-400" />
        )}
      </button>

      {!collapsed && (
        <div className="px-6 pb-6 pt-1 overflow-x-auto">
          <div className="flex items-start min-w-max">
            {milestones.map((m, i) => {
              const prev = milestones[i - 1];
              const duration =
                m.done && prev?.done && m.date && prev.date
                  ? daysBetween(prev.date, m.date)
                  : null;

              return (
                <div key={m.key} className="flex items-center">
                  {/* Connector + duration */}
                  {i > 0 && (
                    <div className="flex flex-col items-center mx-1 mb-8">
                      <div
                        className={`h-0.5 w-12 ${m.done ? "bg-gray-300 dark:bg-slate-600" : "bg-gray-100 dark:bg-slate-700 border-dashed"}`}
                      />
                      {duration !== null && (
                        <span className="text-[9px] text-gray-400 dark:text-slate-500 mt-1 font-medium">
                          {duration}d
                        </span>
                      )}
                    </div>
                  )}

                  {/* Milestone node */}
                  <div className="flex flex-col items-center w-24">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0 transition-all ${
                        m.done ? m.dotColor : "bg-gray-200 dark:bg-slate-600"
                      }`}
                    >
                      {m.icon}
                    </div>
                    <p
                      className={`text-xs font-bold mt-2 text-center ${
                        m.done
                          ? "text-slate-700 dark:text-slate-200"
                          : "text-slate-400 dark:text-slate-500"
                      }`}
                    >
                      {m.label}
                    </p>
                    {m.date && m.done ? (
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 text-center">
                        {fmt(m.date)}
                      </p>
                    ) : !m.done ? (
                      <p className="text-[10px] text-slate-300 dark:text-slate-600 mt-0.5">
                        pending
                      </p>
                    ) : null}
                    {m.key === "inprogress" && m.interruptions && m.interruptions > 0 ? (
                      <div className="flex items-center gap-1 mt-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded px-1.5 py-0.5">
                        <AlertTriangle size={9} className="text-amber-500" />
                        <span className="text-[9px] text-amber-600 dark:text-amber-400 font-bold">
                          {m.interruptions}x blocked
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
